import { Component, ChangeDetectionStrategy, inject, OnInit, OnDestroy } from '@angular/core';
import { SectionComponent } from '../../components/section/section.component';
import { YouTubeService, YouTubeVideoMetadata } from '../../../app/providers/youtube.service';
import { EMPTY, ReplaySubject, Subject, catchError, combineLatest, map, shareReplay, startWith, switchMap } from 'rxjs';
import { iconArrowRight, iconArrowsRight } from '@knorr-bremse-portals/icons';
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { HttpErrorResponse } from '@angular/common/http';
import { EditorIdDirective } from '../../../app/directives/editor-id.directive';
import { CommonModule } from '@angular/common';
import { UserConsentDialogComponent } from '../../../app/components/user-consent-dialog/user-consent-dialog.component';
import { SafePipe } from '../../../app/providers/safe.pipe';
import { Section, SectionAttributes } from '../../../app/types/section';
import { ButtonComponent } from '@knorr-bremse-portals/ngx-components/components/button';
import { LineClampComponent } from '@knorr-bremse-portals/ngx-components/components/line-clamp';
import { ProgressIndicatorComponent } from '@knorr-bremse-portals/ngx-components/components/progress-indicator';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';

enum ErrorType {
  NotFound,
  Connection,
  Unknown,
}

const errorMessages = {
  [ErrorType.NotFound]: $localize `:YouTube playlist not found@@1995660446193743439:Playlist not found`,
  [ErrorType.Connection]: $localize `:YouTube playlist can not connect@@1617620520808343563:Playlist could not be loaded, unable to connect to server`,
  [ErrorType.Unknown]: $localize `:YouTube playlist unknown error@@4220834044378909118:Playlist could not be loaded`,
}

type YouTubePlaylistSection = Section<
  {
    numColumns: 12;
    headline: string;
    playlistId: string;
  } & SectionAttributes,
  [
    'numColumns',
    'headline',
    'playlistId',
  ],
  'youtube-playlist'
>;

@Component({
    selector: 'app-youtube-playlist',
    styleUrls: ['./youtube-playlist.component.scss'],
    templateUrl: './youtube-playlist.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule, EditorIdDirective,
        UserConsentDialogComponent, SafePipe,
        ButtonComponent,
        LineClampComponent,
        ProgressIndicatorComponent,
        IconComponent,
        FsButtonComponent,
    ],
})
export class YouTubePlaylistComponent extends SectionComponent<YouTubePlaylistSection> implements OnInit, OnDestroy {

  protected iconArrowsRight = iconArrowsRight;
  protected iconPlay = iconArrowRight;

  protected playlistId$ = new ReplaySubject<string>(1);

  private youTubeService = inject(YouTubeService);
  protected userConsent = this.youTubeService.userConsent;
  protected getEmbedUrl = YouTubeService.getEmbedUrl;
  protected getThumbnailUrl = this.youTubeService.getThumbnailUrl.bind(this.youTubeService);
  protected getPlaylistUrl = YouTubeService.getPlaylistUrl;

  protected errorMessage$ = new ReplaySubject<string>(1);
  protected playlist$ = this.playlistId$.pipe(
    switchMap(playlistId => this.youTubeService.getPlaylistMetadata(playlistId)),
    catchError((error) => {
      let errorType: ErrorType = ErrorType.Unknown;
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) {
          errorType = ErrorType.NotFound;
        } else if (error.status === 0) {
          errorType = ErrorType.Connection;
        }
      }
      this.errorMessage$.next(errorMessages[errorType]);
      return EMPTY;
    }),
  );
  private activeVideoId$ = new Subject<string>();
  protected activeVideo$ = combineLatest([
    this.playlist$,
    this.activeVideoId$.pipe(startWith(undefined)),
  ]).pipe(
    map(([playlist, activeVideoId]) => {
      if (activeVideoId === undefined) {
        return playlist.videos[0];
      }

      return playlist.videos.find((video) => video.id === activeVideoId);
    }),
    shareReplay(1),
  );

  public override ngOnInit(): void {
    super.ngOnInit();
    this.playlistId$.next(this.attributes.playlistId);
  }

  public ngOnDestroy(): void {
    this.playlistId$.complete();
    this.activeVideoId$.complete();
    this.errorMessage$.complete();
  }

  protected setActive(video: YouTubeVideoMetadata): void {
    this.activeVideoId$.next(video.id);
  }

}
